import React, {
  MouseEvent,
  RefObject,
  useRef,
} from 'react';
import clsx from 'clsx';
import IconButton from '@mui/material/IconButton';
import dayjs from 'utils/dayjs';
import isNil from 'lodash/isNil';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import {
  EncounterViewModel,
} from 'dtos';
import {
  formatTime,
} from 'utils/date';
import {
  useMedia,
} from 'core/hooks';
import Image from 'pages/Dashboard/components/Image';
import ToolTip from 'core/components/Tooltip';
import Name from 'pages/Dashboard/components/Name';
import AppointmentStatusSelect from 'pages/Dashboard/components/AppointmentStatusSelect';
import {
  useAppointmentStatusColors,
} from 'pages/Dashboard/pages/Encounters/hooks';
import Tag from 'core/components/Tag';
import WaitTime from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientRow/WaitTime';
import ProviderInitialsBadge from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientRow/ProviderInitialsBadge';
import {
  EncounterDoctor,
} from 'pages/Dashboard/pages/Encounters/types/settings';
import {
  toDosEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  TaskIcon,
} from 'core/icons';
import {
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';
import {
  getDisplayName,
  getInitials,
} from 'pages/Dashboard/utils/helper';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';
import {
  hasTelehealthMeeting,
} from 'utils/telehealth';

type Props = {
  encounter: EncounterViewModel;
  provider?: EncounterDoctor;
  selected: boolean;
  onClick: (e: MouseEvent<HTMLElement>) => void;
  refetch?: () => void;
};

type PType = HTMLParagraphElement;

const noteFinalizedRegex = /note finalized/i;

export default function PatientRow({
  encounter,
  provider,
  selected,
  onClick,
  refetch,
}: Props) {
  const { isRestricted: hasLimitedAccess } = useEMRAccess();
  const {
    reasonForVisit,
    patient,
    startDateTime = '',
    typeOfVisitDescription = '',
    durationInMinutes,
    patientEnteredOfficeAt,
    patientAppointmentStatus: appointmentStatus,
    isAppointmentStatusOutOfOffice,
    isTelehealth,
    telehealthProviderJoinInfo,
  } = encounter;

  const meetingExists = hasTelehealthMeeting(telehealthProviderJoinInfo);
  const isNoteFinalized = noteFinalizedRegex.test(appointmentStatus ?? '');
  const showMissingTelehealthMeeting = isTelehealth === true && !meetingExists;

  const { getMediaPathById } = useMedia();
  const fullNameContainerRef = useRef<HTMLElement>(null) as RefObject<HTMLHeadingElement>;
  const chiefComplaintContainerRef = useRef<PType>(null) as RefObject<PType>;

  const classes = clsx(
    'w-full h-full p-2 cursor-pointer flex justify-between items-center text-gray-light bg-white rounded before:pointer-events-none',
    'before:content[\'\'] before:absolute before:bottom-0 before:inset-x-0 before:border-b before:border-b-gray-200 after:pointer-events-none',
    {
      'text-blue-500 after:absolute after:inset-0 after:content[\'\'] after:block': selected,
      'after:w-full after:border after:rounded after:border-gray-500': selected,
    },
  );
  const fullNameClasses = clsx('text-sm font-semibold truncate', {
    'text-inherit': selected,
    'text-black': !selected,
  });

  const colors = useAppointmentStatusColors();
  const statusColor = colors[appointmentStatus ?? ''] ?? '';
  const patientDisplayName = getDisplayName(patient?.formattedNames);

  return (
    <div
      className={classes}
      role="button"
      tabIndex={-1}
      onClick={onClick}
      onKeyDown={() => { }}
      data-nw-attr={patientDisplayName}
    >
      <div className="w-full items-center flex space-x-2">
        <div className="flex flex-col text-sm text-black w-18 min-w-[72px]">
          <p className="w-auto nw-start-time">
            {formatTime(dayjs(startDateTime))}
          </p>
          <p className="text-gray-400 text-xs nw-end-time">
            {formatTime(dayjs(startDateTime).add(durationInMinutes ?? 0, 'm'))}
          </p>
        </div>
        <Image
          classes={{ root: 'small:!hidden' }}
          src={getMediaPathById(patient?.patientId ?? 0) ?? undefined}
          size="md"
          alt={patientDisplayName}
        >
          {getInitials(patient?.formattedNames)}
        </Image>
        <div className="flex flex-col min-w-0 flex-1">
          <div className="flex w-full gap-2 items-center justify-between min-w-0">
            <div className="flex flex-1 items-center gap-2 min-w-0">
              <ToolTip
                title={patientDisplayName}
                auto
                ref={fullNameContainerRef}
              >
                <span
                  className="flex min-w-0 flex-1"
                  ref={fullNameContainerRef}
                >
                  <Name
                    className={clsx('min-w-0', {
                      'pointer-events-none': !selected,
                    }, 'nw-patient-name')}
                    labelClassName={fullNameClasses}
                    link={getPatientChartPath(patient?.patientId)}
                    {...patient?.formattedNames}
                  />
                </span>
              </ToolTip>
              {!isAppointmentStatusOutOfOffice && (
                <div className="flex items-center shrink-0">
                  <WaitTime patientEnteredInOfficeTime={patientEnteredOfficeAt ?? ''} />
                </div>
              )}
              {!isNil(provider) && (
                <div className="flex items-center shrink-0 pl-2 ml-0.5">
                  <ProviderInitialsBadge
                    name={provider.name}
                    initials={provider.initials}
                    userId={provider.userId}
                  />
                </div>
              )}
            </div>
            {!hasLimitedAccess && (
              <ToolTip title="Create To-Do">
                <IconButton
                  size="small"
                  color="info"
                  className="shrink-0"
                  onClick={(event) => {
                    event.stopPropagation();
                    toDosEventChannel.emit('onCreateToDo', { patient });
                  }}
                >
                  <TaskIcon className="!w-4 !h-4 !text-blue-500" />
                </IconButton>
              </ToolTip>
            )}
          </div>
          <div className="flex justify-between gap-2 w-full items-center relative mt-0.5">
            <div className="flex flex-1 items-center gap-2 min-w-0">
              <Tag
                className="!inline-block !leading-[18px] !max-w-[160px] truncate"
                tagName={typeOfVisitDescription ?? ''}
                tooltip={typeOfVisitDescription ?? ''}
                responsive
              />
              {isTelehealth && meetingExists && (
                <ToolTip title="Telehealth meeting is scheduled">
                  <span className="flex items-center shrink-0 text-green-600">
                    <VideocamOutlinedIcon className="!h-5 !w-5" />
                  </span>
                </ToolTip>
              )}
              {showMissingTelehealthMeeting && (
                <ToolTip title="Telehealth meeting creation pending">
                  <span className="flex items-center shrink-0 text-red-500">
                    <VideocamOutlinedIcon className="!h-5 !w-5" />
                  </span>
                </ToolTip>
              )}
              {!isNil(reasonForVisit) && (
                <ToolTip
                  title={reasonForVisit}
                  placement="bottom-start"
                  auto
                  ref={chiefComplaintContainerRef}
                >
                  <p
                    className="flex-1 text-xs text-gray-light truncate max-w-fit mr-4"
                    ref={chiefComplaintContainerRef}
                  >
                    {reasonForVisit}
                  </p>
                </ToolTip>
              )}
            </div>
            <AppointmentStatusSelect
              encounterId={encounter.encounterId}
              value={appointmentStatus ?? ''}
              appointmentDate={startDateTime}
              disabled={hasLimitedAccess || isNoteFinalized}
              statusColor={statusColor}
              onUpdate={async () => { await refetch?.(); }}
              classes={{
                root: '!w-fit nw-encounter-status shrink-0',
                select: clsx('!py-0 rounded !font-medium', {
                  'capitalize': hasLimitedAccess,
                }),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
